import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { FiAlertCircle, FiArrowLeft, FiCheckCircle, FiLock, FiX } from 'react-icons/fi';
import authService from '../utils/auth.js';
import AuthShell from './AuthShell.jsx';

function ResetPassword() {
  const { token: tokenParam } = useParams();
  const [searchParams] = useSearchParams();
  const token = String(tokenParam || searchParams.get('token') || '').replace(/\s+/g, '');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      await authService.resetPassword(token, password, searchParams.get('c'));
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const mismatch = confirmPassword && password !== confirmPassword;

  return (
    <AuthShell>
      <div className="auth-card">
        <div className="auth-header">
          <span className="auth-icon" aria-hidden="true"><FiLock /></span>
          <h1>Set a new password</h1>
          <p>Choose a strong password you haven&rsquo;t used before</p>
        </div>

        {error && (
          <div className="auth-alert auth-alert-error" role="alert">
            <FiAlertCircle className="auth-alert-icon" aria-hidden="true" />
            <span className="auth-alert-text">{error}</span>
            <button type="button" onClick={() => setError('')} className="auth-alert-close" aria-label="Dismiss"><FiX /></button>
          </div>
        )}

        {success ? (
          <div className="auth-result">
            <span className="auth-result-icon is-success" aria-hidden="true"><FiCheckCircle /></span>
            <div className="auth-alert auth-alert-success" role="status">
              <span className="auth-alert-text">Password reset successfully! Redirecting to login...</span>
            </div>
            <div className="auth-links">
              <Link to="/login" className="auth-link">Go to Login</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-form-group">
              <label htmlFor="password">New password</label>
              <div className="auth-input">
                <FiLock aria-hidden="true" />
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  autoComplete="new-password"
                  placeholder="At least 6 characters"
                />
              </div>
            </div>
            <div className="auth-form-group">
              <label htmlFor="confirmPassword">Confirm new password</label>
              <div className={`auth-input${mismatch ? ' is-invalid' : ''}`}>
                <FiLock aria-hidden="true" />
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  aria-invalid={mismatch ? 'true' : undefined}
                  placeholder="Re-enter your password"
                />
              </div>
              {mismatch && (
                <p className="auth-help-text auth-field-error"><FiAlertCircle aria-hidden="true" /> Passwords do not match</p>
              )}
            </div>
            <button type="submit" className="auth-button auth-button-primary" disabled={loading}>
              {loading && <span className="auth-spinner" aria-hidden="true" />}
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <div className="auth-links">
              <Link to="/login" className="auth-link"><FiArrowLeft aria-hidden="true" /> Back to login</Link>
            </div>
          </form>
        )}
      </div>
    </AuthShell>
  );
}

export default ResetPassword;
